import type { BuildingSelection, Plan, Project } from './types';

export function selectionSources(project: Project, plan: Plan): BuildingSelection[] {
  return (plan.selectionIds ?? []).flatMap((id) => {
    const source = plan.selectionSources?.find((item) => item.id === id)
      ?? project.selections.find((item) => item.id === id);
    return source ? [source] : [];
  });
}

export function composition(project: Project, plan: Plan) {
  const sources = selectionSources(project, plan);
  const union = new Set(sources.flatMap((source) => source.buildingIds));
  const ids = new Set(plan.buildingIds ?? []);
  return {
    sources,
    added: [...new Set([...(plan.manualBuildingIds ?? []).filter((id) => ids.has(id)), ...[...ids].filter((id) => !union.has(id))])],
    excluded: [...union].filter((id) => !ids.has(id)),
  };
}

export function changeSelection(project: Project, plan: Plan, id: string, checked: boolean): Partial<Plan> {
  const { sources, added, excluded } = composition(project, plan);
  const selection = project.selections.find((item) => item.id === id);
  const next = checked && selection
    ? [...sources.filter((source) => source.id !== id), structuredClone(selection)]
    : sources.filter((source) => source.id !== id);
  const excludedIds = new Set([...(plan.excludedBuildingIds ?? []), ...excluded]);
  return {
    selectionIds: next.map((source) => source.id),
    selectionSources: next,
    manualBuildingIds: added,
    excludedBuildingIds: [...excludedIds],
    buildingIds: [...new Set([...next.flatMap((source) => source.buildingIds), ...added])]
      .filter((buildingId) => !excludedIds.has(buildingId)),
  };
}

export function updateBuildingIds(project: Project, plan: Plan, buildingIds: string[]): Partial<Plan> {
  const { sources, added, excluded } = composition(project, plan);
  const union = new Set(sources.flatMap((source) => source.buildingIds));
  const next = new Set(buildingIds);
  const exclusions = new Set([...(plan.excludedBuildingIds ?? []), ...excluded]);
  for (const id of union) {
    if (next.has(id)) exclusions.delete(id);
    else exclusions.add(id);
  }
  for (const id of next) exclusions.delete(id);
  return {
    buildingIds: [...next],
    manualBuildingIds: [...new Set([...added.filter((id) => next.has(id)), ...[...next].filter((id) => !union.has(id))])],
    excludedBuildingIds: [...exclusions],
  };
}
